import PropTypes from 'prop-types';
import { COLORS, TYPOGRAPHY } from '../../../styles/UISystem';
import { ALL_CONTACT_TYPES, getBaseType } from '../../../utils/contactTypeConfig';
import { formatPhoneNumber } from '../../../utils/formatters';

/**
 * ContactShareBubble Component
 *
 * Displays shared contact information in a chat bubble format.
 * Shows stacked contact rows with icons and clickable values.
 *
 * Used when Profile user shares contacts in chat.
 * Note: Greeting messages are now sent as separate chat messages, not included here.
 *
 * @param {Array} contacts - Array of contact objects: [{ type, value, displayValue }]
 * @param {boolean} isSent - true for sent (Profile view), false for received (Anon view)
 */
export const ContactShareBubble = ({ contacts, isSent = true, disableLinks = false }) => {
  // Get icon component for contact type
  // Returns null for self-identifying types (phone, email) - no icon needed
  const getIconForType = (type) => {
    // Phone and email are self-identifying - don't show icons
    if (type === 'phone' || type === 'phone2' || type === 'email' || type === 'email2') {
      return null;
    }

    const baseType = getBaseType(type);
    const config = ALL_CONTACT_TYPES[baseType];
    return config?.Icon || ALL_CONTACT_TYPES.custom.Icon;
  };

  // Format display value - phone numbers get consistent (xxx) xxx-xxxx format
  // Instagram/TikTok/Twitter get @ prefix
  const getDisplayValue = (contact) => {
    const { type, value, displayValue } = contact;

    // For phone types, format consistently
    if (type === 'phone' || type === 'phone2' || type === 'whatsapp' || type === 'whatsapp2') {
      return formatPhoneNumber(value);
    }

    // For @ prefix types, prepend @ (strip first if already present)
    if (type === 'instagram' || type === 'instagram2' || type === 'tiktok' || type === 'twitter') {
      const handle = (displayValue || value || '').replace(/^@/, '');
      return `@${handle}`;
    }

    // For other types, use displayValue if provided, otherwise value
    return displayValue || value;
  };

  // Generate clickable URL for contact type
  const getContactUrl = (type, value) => {
    if (!value) return null;

    // Clean phone number for sms: link
    const cleanPhone = (phone) => phone.replace(/[^+\d]/g, '');

    switch (type) {
      case 'phone':
      case 'phone2':
        return `sms:${cleanPhone(value)}`;
      case 'email':
      case 'email2':
        return `mailto:${value}`;
      case 'whatsapp':
      case 'whatsapp2':
        return `https://wa.me/${cleanPhone(value)}`;
      case 'instagram':
      case 'instagram2': {
        const handle = value.startsWith('@') ? value.slice(1) : value;
        return `https://instagram.com/${handle}`;
      }
      case 'linkedin':
        return value.startsWith('http') ? value : `https://linkedin.com/in/${value}`;
      case 'facebook':
        return value.startsWith('http') ? value : `https://facebook.com/${value}`;
      case 'github':
        return value.startsWith('http') ? value : `https://github.com/${value}`;
      case 'twitter':
        return value.startsWith('http') ? value : `https://twitter.com/${value}`;
      case 'snapchat':
        return `https://snapchat.com/add/${value}`;
      case 'tiktok': {
        const tiktokHandle = value.startsWith('@') ? value.slice(1) : value;
        return `https://tiktok.com/@${tiktokHandle}`;
      }
      case 'youtube':
        return value.startsWith('http') ? value : `https://youtube.com/@${value}`;
      case 'twitch':
        return value.startsWith('http') ? value : `https://twitch.tv/${value}`;
      case 'telegram':
        return value.startsWith('http') ? value : `https://t.me/${value}`;
      case 'signal':
        // Signal doesn't have public profile URLs, use SMS
        return `sms:${cleanPhone(value)}`;
      case 'substack':
        return value.startsWith('http') ? value : `https://${value}.substack.com`;
      case 'custom':
        return value.startsWith('http') ? value : `https://${value}`;
      default:
        return null;
    }
  };

  const iconColor = isSent ? COLORS.WHITE : '#5A5A5A';
  const textColor = isSent ? COLORS.WHITE : COLORS.CHAT_TEXT_DARK;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        marginBottom: '14px',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderRadius: isSent ? '18px 18px 0 18px' : '18px 18px 18px 0',
          backgroundColor: isSent ? COLORS.CHAT_SENT : COLORS.RECEIVED_GRAY,
          maxWidth: '85%',
          wordBreak: 'break-word',
          ...TYPOGRAPHY.BODY_REGULAR,
          color: textColor,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {contacts.map((contact, index) => {
            const Icon = getIconForType(contact.type);
            const url = getContactUrl(contact.type, contact.value);
            const displayValue = getDisplayValue(contact);

            return (
              <div
                key={`${contact.type}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: Icon ? '10px' : '0',
                }}
              >
                {Icon && <Icon size={20} color={iconColor} weight="regular" />}
                {url && !disableLinks ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...TYPOGRAPHY.BODY_REGULAR,
                      // All linkable contacts: white underlined on sent (blue bubble), blue underlined on received
                      color: isSent ? COLORS.WHITE : '#007AFF',
                      textDecoration: 'underline',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {displayValue}
                  </a>
                ) : (
                  <span style={{ ...TYPOGRAPHY.BODY_REGULAR, color: 'inherit' }}>
                    {displayValue}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

ContactShareBubble.propTypes = {
  /** Array of contact objects to display */
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      displayValue: PropTypes.string,
    })
  ).isRequired,
  /** Whether the message is sent (blue background) or received (gray background) */
  isSent: PropTypes.bool,
  /** When true, render contact values as plain text instead of clickable links */
  disableLinks: PropTypes.bool,
};

export default ContactShareBubble;
